import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Card, Text, Loading } from "@nextui-org/react";
import { motion } from "framer-motion";
import { database } from "../firebase";

import AltAnalyticsHeader from "../components/alt-analytics/AltAnalyticsHeader";
import AltAnalyticsDateFilterCard from "../components/alt-analytics/AltAnalyticsDateFilterCard";
import AltAnalyticsKPIGrid from "../components/alt-analytics/AltAnalyticsKPIGrid";
import AltAnalyticsTable from "../components/alt-analytics/AltAnalyticsTable";
import { downloadAltAnalyticsExcel } from "../utils/altEntriesExport";

const getFirstDayOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const AltEntriesAnalytics = () => {
  const [entries, setEntries] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [row] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: "created", asc: false });

  // Input date states
  const [fromDate, setFromDate] = useState(getFirstDayOfMonth());
  const [toDate, setToDate] = useState(getToday());

  // Applied date states used for filtering
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(false);
      const [entriesSnap, deductionsSnap] = await Promise.all([
        database.collection("alt_entries").orderBy("created", "desc").get(),
        database
          .collection("alt_deductions")
          .orderBy("createdAt", "desc")
          .get(),
      ]);

      const entryDocs = entriesSnap.docs.map((d) => ({
        _id: d.id,
        recordType: "entry",
        ...d.data(),
      }));

      const deductionDocs = deductionsSnap.docs.map((d) => ({
        _id: d.id,
        recordType: "deduction",
        ...d.data(),
      }));

      setEntries(entryDocs);
      setDeductions(deductionDocs);
      setAppliedFromDate(fromDate);
      setAppliedToDate(toDate);
      setHasSearched(true);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Do not fetch on initial load automatically so fetching only happens after clicking button
  useEffect(() => {
    setLoading(false);
  }, []);

  const combinedList = useMemo(() => {
    return [...entries, ...deductions];
  }, [entries, deductions]);

  const filteredList = useMemo(() => {
    if (!hasSearched) return [];
    if (!appliedFromDate && !appliedToDate) return combinedList;

    const start = appliedFromDate ? new Date(appliedFromDate + "T00:00:00").getTime() : 0;
    const end = appliedToDate ? new Date(appliedToDate + "T23:59:59").getTime() : Infinity;

    return combinedList.filter((doc) => {
      const rawDate = doc.created || doc.createdAt;
      if (!rawDate) return false;
      const dTime = new Date(rawDate).getTime();
      return dTime >= start && dTime <= end;
    });
  }, [combinedList, appliedFromDate, appliedToDate, hasSearched]);

  const processedList = useMemo(() => {
    let list = [...filteredList];
    const { key, asc } = sortConfig;
    list.sort((a, b) => {
      let valA, valB;
      const isDeductionA =
        a.type === "deduction" || a.recordType === "deduction";
      const isDeductionB =
        b.type === "deduction" || b.recordType === "deduction";

      if (key === "amount") {
        valA = isDeductionA ? -Number(a.amount || 0) : Number(a.amount || 0) * Number(a.qty || 1);
        valB = isDeductionB ? -Number(b.amount || 0) : Number(b.amount || 0) * Number(b.qty || 1);
      } else if (key === "customerAmount") {
        valA = isDeductionA ? 0 : Number(a.customerAmount || 0) * Number(a.qty || 1);
        valB = isDeductionB ? 0 : Number(b.customerAmount || 0) * Number(b.qty || 1);
      } else if (key === "profit") {
        valA = isDeductionA && a.category !== "customer" ? 0 : (Number(a.customerAmount || 0) - Number(a.amount || 0)) * Number(a.qty || 1);
        valB = isDeductionB && b.category !== "customer" ? 0 : (Number(b.customerAmount || 0) - Number(b.amount || 0)) * Number(b.qty || 1);
        if (isDeductionA || a.category !== "customer") valA = 0;
        if (isDeductionB || b.category !== "customer") valB = 0;
      } else if (key === "created") {
        valA = new Date(a.created || a.createdAt || 0).getTime();
        valB = new Date(b.created || b.createdAt || 0).getTime();
      } else {
        valA = (a[key] || "").toString().toLowerCase();
        valB = (b[key] || "").toString().toLowerCase();
      }

      if (typeof valA === "string") {
        return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return asc ? valA - valB : valB - valA;
    });

    return list;
  }, [filteredList, sortConfig]);

  const metrics = useMemo(() => {
    let shopTotal = 0;
    let sunilC = 0;
    let customerTotal = 0;
    let deductTotal = 0;
    let entryCount = 0;

    filteredList.forEach((doc) => {
      const isDeduction =
        doc.type === "deduction" || doc.recordType === "deduction";
      if (isDeduction) {
        deductTotal += Number(doc.amount || 0);
      } else {
        const q = Number(doc.qty || 1);
        if (doc.category === "shop") {
          shopTotal += Number(doc.amount || 0) * q;
        } else if (doc.category === "customer") {
          sunilC += Number(doc.amount || 0) * q;
        }
        customerTotal += Number(doc.customerAmount || 0) * q;
        entryCount++;
      }
    });

    const netShopTotal = shopTotal - deductTotal;
    const profit = customerTotal - sunilC;

    return {
      shopTotal: netShopTotal,
      sunilC,
      customerTotal,
      profit,
      deductTotal,
      entryCount,
    };
  }, [filteredList]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      asc: prev.key === key ? !prev.asc : true,
    }));
  };

  return (
    <motion.div>
      {/* Header */}
      <AltAnalyticsHeader
        onExport={() => downloadAltAnalyticsExcel(processedList)}
      />

      {/* Date Filter Card */}
      <AltAnalyticsDateFilterCard
        fromDate={fromDate}
        onFromDateChange={setFromDate}
        toDate={toDate}
        onToDateChange={setToDate}
        onFetchReport={fetchData}
        loading={loading}
      />

      {/* KPI Cards Grid */}
      {!loading && !error && <AltAnalyticsKPIGrid metrics={metrics} />}

      {/* Table Container */}
      <motion.div
        className="alignDivCenter"
        style={{ marginTop: "5px", paddingTop: "0px" }}
      >
        {loading ? (
          <Loading type="spinner" />
        ) : (
          <Fragment>
            {error && (
              <Text color="error">An error occurred while loading analytics!</Text>
            )}

            <Card variant="bordered" className="cardShadow">
              <Card.Body css={{ p: 0 }}>
                <AltAnalyticsTable
                  processedList={processedList}
                  row={row}
                  onSort={handleSort}
                />
              </Card.Body>
            </Card>
          </Fragment>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AltEntriesAnalytics;

